import React, { useState, useRef } from 'react';
import { LayoutDashboard, Users, Settings, ChevronRight, ChevronDown, Check, LogOut, BookOpen, FileText, UserCog, ClipboardCheck, GraduationCap } from 'lucide-react';
import { type Classroom } from '../../accounts';
import { DjayNarrator } from '../../mascot';
import { useClickOutside } from '../../app/hooks/useClickOutside';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { CoBadge } from './components/CoBadge';
export type TeacherV2Route = 'today' | 'class' | 'homework' | 'subjects' | 'assessments' | 'reports' | 'management' | 'settings';
type NavGroup = 'Vandaag' | 'Mijn klas' | 'Inzicht' | 'Beheer';
interface NavItem {
  id: TeacherV2Route;
  label: string;
  icon: React.ReactNode;
  group: NavGroup;
}
const NAV_ITEMS: NavItem[] = [{
  id: 'today',
  label: 'Vandaag',
  icon: <LayoutDashboard className="w-5 h-5" data-id="element-11056" data-mp="2gjqdkzuefsbr" />,
  group: 'Vandaag'
}, {
  id: 'class',
  label: 'Mijn Klas',
  icon: <Users className="w-5 h-5" data-id="element-11057" data-mp="2bhr8nlj0gncp" />,
  group: 'Mijn klas'
}, {
  id: 'homework',
  label: 'Huiswerk',
  icon: <GraduationCap className="w-5 h-5" data-id="element-11058" data-mp="51brg8ws06wp" />,
  group: 'Mijn klas'
}, {
  id: 'assessments',
  label: 'Toetsen',
  icon: <ClipboardCheck className="w-5 h-5" data-id="element-11059" data-mp="9xiy63wzxmfp" />,
  group: 'Mijn klas'
}, {
  id: 'subjects',
  label: 'Vakken',
  icon: <BookOpen className="w-5 h-5" data-id="element-11060" data-mp="3s7kz0j67fkju" />,
  group: 'Inzicht'
}, {
  id: 'reports',
  label: 'Rapporten',
  icon: <FileText className="w-5 h-5" data-id="element-11061" data-mp="3iff7fajv7556" />,
  group: 'Inzicht'
}, {
  id: 'management',
  label: 'Klas Beheer',
  icon: <UserCog className="w-5 h-5" data-id="element-11062" data-mp="3n5vkgtjrwor0" />,
  group: 'Beheer'
}];
interface TeacherSidebarV2Props {
  activeRoute: TeacherV2Route;
  onNavigate: (route: TeacherV2Route) => void;
  onLogout?: () => void;
  classroomName?: string;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  onSelectClassroom?: (id: string | null) => void;
  todayAttentionCount?: number;
  /** Active teacher account id — used to mark co-teacher classrooms with a 'Co' badge. */
  activeTeacherId?: string;
}
export function TeacherSidebarV2({
  activeRoute,
  onNavigate,
  onLogout,
  classroomName = 'Mijn Klas',
  classrooms = [],
  selectedClassroomId = null,
  onSelectClassroom,
  todayAttentionCount = 0,
  activeTeacherId
}: TeacherSidebarV2Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [showClassroomDropdown, setShowClassroomDropdown] = useState(false);
  const classroomDropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(classroomDropdownRef, () => {
    if (showClassroomDropdown) setShowClassroomDropdown(false);
  });
  useEscapeKey(() => {
    if (showClassroomDropdown) setShowClassroomDropdown(false);
  });
  const activeClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const displayClassName = activeClassroom ? activeClassroom.name : classrooms.length > 0 ? 'Alle klassen' : classroomName;
  const isCoTeach = (c: Classroom) => !!activeTeacherId && c.teacherId !== activeTeacherId;
  const activeIsCoTeach = activeClassroom ? isCoTeach(activeClassroom) : false;
  return <aside className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-300 flex-shrink-0 h-full ${collapsed ? 'w-20' : 'w-72'}`} data-id="element-11063" data-mp="3pmdsjj18e253">
      
      {/* Header / Logo */}
      <div className={`flex items-center border-b border-slate-100 flex-shrink-0 h-20 ${collapsed ? 'justify-center px-2' : 'justify-between px-6'}`} data-id="element-11064" data-mp="3cufzsm06anc">
        
        {!collapsed && <div className="flex items-center gap-3" data-id="element-11065" data-mp="c6pistznjozt">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center border border-indigo-100 shadow-sm overflow-hidden" data-id="element-11066" data-mp="c8aeimxpddz9">
              <DjayNarrator size="xs" emotion="happy" data-id="element-11067" data-mp="m55bv2i8p8by" />
            </div>
            <div className="flex flex-col" data-id="element-11068" data-mp="r2x11ori2gcg">
              <span className="text-sm font-black text-slate-900 leading-tight tracking-tight" data-id="element-11069" data-mp="r2ooxz37bf2b">
                Meester djay
              </span>
              <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-11070" data-mp="2sujzgbdq1diy">
                Docentportaal
              </span>
            </div>
          </div>}
        {collapsed && <div className="relative w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center border border-indigo-100 shadow-sm overflow-hidden group" data-id="element-11071" data-mp="n844oln6k7b8">
            <DjayNarrator size="xs" emotion="happy" data-id="element-11072" data-mp="w5o3zp1hftj7" />
            {activeClassroom && <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-indigo-600 text-white text-[8px] font-bold rounded-full flex items-center justify-center border border-white" title={activeClassroom.name} data-id="element-11073" data-mp="r7mpziiwykel">
            
                {activeClassroom.groupLevel}
              </div>}
          </div>}
        {!collapsed && <button onClick={() => setCollapsed(true)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none" aria-label="Inklappen" data-id="element-11074" data-mp="d8vdg0jp96sk">
          
            <ChevronRight className="w-4 h-4 rotate-180" data-id="element-11075" data-mp="3byn5hmhrmxu6" />
          </button>}
      </div>

      {/* Classroom Selector */}
      {!collapsed && classrooms.length > 0 && onSelectClassroom && <div ref={classroomDropdownRef} className="px-4 py-4 border-b border-slate-100 relative" data-id="element-11076" data-mp="1w6rl3dvitji8">
        
          <button onClick={() => setShowClassroomDropdown(!showClassroomDropdown)} className="w-full flex items-center justify-between bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm" data-id="element-11077" data-mp="1ifmtmz2b3xiz">
          
            <div className="flex flex-col items-start overflow-hidden" data-id="element-11078" data-mp="2f5dncw2rxgnq">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-11079" data-mp="2f3b0gkwx43n8">
                Huidige weergave
              </span>
              <div className="flex items-center gap-2 w-full" data-id="element-11080" data-mp="263wd3uc1s96y">
                <span className="text-sm font-bold text-slate-900 truncate text-left" data-id="element-11081" data-mp="pqz4gsefso05">
                  {displayClassName}
                </span>
                {activeIsCoTeach && <CoBadge data-id="element-11082" data-mp="unpila6ol9ix" />}
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showClassroomDropdown ? 'rotate-180' : ''}`} data-id="element-11083" data-mp="ammle2033jhu" />
          
          </button>

          {showClassroomDropdown && <div className="absolute top-full left-4 right-4 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg z-20 overflow-hidden py-1.5" data-id="element-11084" data-mp="1uknaadhmfaq">
              <button onClick={() => {
          onSelectClassroom(null);
          setShowClassroomDropdown(false);
        }} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === null ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-11085" data-mp="3szkoghs6asg8">
            
                <span data-id="element-11086" data-mp="3a3ujcloy1fy4">Alle klassen</span>
                {selectedClassroomId === null && <Check className="w-4 h-4" data-id="element-11087" data-mp="1db2kwdstmnyy" />}
              </button>
              <div className="h-px bg-slate-100 my-1.5 mx-4" data-id="element-11088" data-mp="1d86mypgz3zai" />
              {classrooms.map(c => <button key={c.id} onClick={() => {
          onSelectClassroom(c.id);
          setShowClassroomDropdown(false);
        }} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === c.id ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-11089" data-mp="18c7oua5uf2ww">
            
                  <div className="flex items-center gap-2 truncate min-w-0" data-id="element-11090" data-mp="1uijzml5sluek">
                    <span className="truncate" data-id="element-11091" data-mp="wwwdqu6yquha">{c.name}</span>
                    {isCoTeach(c) && <CoBadge data-id="element-11092" data-mp="11uxv9aoqqlqn" />}
                    <span className="text-[10px] py-0.5 px-2 bg-slate-100 text-slate-500 rounded-md font-bold flex-shrink-0" data-id="element-11093" data-mp="11w8y98ad3pgp">
                      {c.learnerIds.length}
                    </span>
                  </div>
                  {selectedClassroomId === c.id && <Check className="w-4 h-4 flex-shrink-0" data-id="element-11094" data-mp="2i9fy214r7nus" />}
                </button>)}
            </div>}
        </div>}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-6 px-3" data-id="element-11095" data-mp="2m9zu11dlclfc">
        <div className="space-y-1.5" data-id="element-11096" data-mp="38ckx74urzexf">
          {(() => {
          const groups: NavGroup[] = ['Vandaag', 'Mijn klas', 'Inzicht', 'Beheer'];
          return groups.map((group, index) => {
            const groupItems = NAV_ITEMS.filter(item => item.group === group);
            if (groupItems.length === 0) return null;
            // "Vandaag" is a single item — no group header, no extra divider
            const showHeader = group !== 'Vandaag';
            return <div key={group} className={index > 0 ? 'pt-2' : ''} data-id="element-11097" data-mp="3r1httfdnajit" data-mpi={index}>
                  {index > 0 && <div className="h-px bg-slate-100 my-3 mx-2" data-id="element-11098" data-mp="3m36if07pc0yh" />}
                  {!collapsed && showHeader && <div className="px-4 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-11099" data-mp="4utbu9b59t87">
                      {group}
                    </div>}
                  <div className="space-y-1.5" data-id="element-11100" data-mp="26mxxx45ypwb0">
                    {groupItems.map(item => {
                  const isActive = activeRoute === item.id;
                  const badgeCount = item.id === 'today' ? todayAttentionCount : 0;
                  return <button key={item.id} onClick={() => onNavigate(item.id)} title={collapsed ? item.label : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 relative focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none ${collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} ${isActive ? 'bg-indigo-50 text-indigo-700 font-bold shadow-sm border border-indigo-100/50' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium border border-transparent'}`} data-id="element-11101" data-mp="h7ev16jo7u5y">
                          
                          <span className={`relative flex-shrink-0 transition-colors ${isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}`} data-id="element-11102" data-mp="3kncbfx8fdycq">
                            
                            {item.icon}
                            {badgeCount > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none" data-id="element-11103" data-mp="1itcwn1w3gyva">
                                {badgeCount > 9 ? '9+' : badgeCount}
                              </span>}
                          </span>
                          {!collapsed && <span className="text-sm truncate" data-id="element-11104" data-mp="ohdftdt2wdvz">
                              {item.label}
                            </span>}
                        </button>;
                })}
                  </div>
                </div>;
          });
        })()}
        </div>
      </nav>

      {/* Footer */}
      <div className={`flex-shrink-0 border-t border-slate-100 ${collapsed ? 'p-3' : 'p-5'} space-y-2`} data-id="element-11105" data-mp="1l5jrulsby8ka">
        
        {collapsed && <button onClick={() => setCollapsed(false)} className="w-full flex items-center justify-center p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors mb-2" aria-label="Uitklappen" data-id="element-11106" data-mp="176k37e4thto4">
          
            <ChevronRight className="w-5 h-5" data-id="element-11107" data-mp="qgf6x6qh301" />
          </button>}

        <button onClick={() => onNavigate('settings')} title={collapsed ? 'Instellingen' : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5'} ${activeRoute === 'settings' ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 font-medium'}`} data-id="element-11108" data-mp="on5f90tq2nt">
          
          <Settings className={`w-5 h-5 ${activeRoute === 'settings' ? 'text-indigo-600' : ''}`} data-id="element-11109" data-mp="35pnfpo4vo8rc" />
          
          {!collapsed && <span className="text-sm" data-id="element-11110" data-mp="31ov78x3ty7w4">Instellingen</span>}
        </button>

        {onLogout && <button onClick={onLogout} className={`w-full flex items-center gap-3 rounded-xl text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5 font-medium'}`} title={collapsed ? 'Uitloggen' : undefined} data-id="element-11111" data-mp="3ff6y6sooi0lo">
          
            <LogOut className="w-5 h-5" data-id="element-11112" data-mp="lweokc7pacvl" />
            {!collapsed && <span className="text-sm" data-id="element-11113" data-mp="lvmu54pual6w">Uitloggen</span>}
          </button>}

        {/*
         Hier stond "Leerlingomgeving": een stille accountwissel naar het
         account van een kind, zonder wachtwoord. Verwijderd — zie de
         toelichting in src/app/PortalSwitch.tsx. De prop is óók weg en niet
         alleen losgekoppeld: een optionele prop die nergens meer wordt
         doorgegeven is één regel verwijderd van teruggekeerd gedrag.
         */}
      </div>
    </aside>;
}